import i18n from '@/utils/i18n';
import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  IconButton,
  Icons,
  List,
  ShimmerText,
  Typography,
  View,
  XStack,
  YStack,
} from '@usepepper/sprout-kit-ui';
import {
  CreateOrderGuideFromImageStep,
  fileKey,
} from '@/experiences/sales-portal/blocs/hooks/use-create-order-guide-from-image';
import { createStyles, useTheme } from '@/contexts/theme-context';
import { EmptyState } from '@experiences/components/empty-state';
import { ErrorState } from '@/experiences/sales-portal/components/error-state';
import { FileDropzone } from '@/experiences/sales-portal/components/file-dropzone.web';
import { FlatList, Image } from 'react-native';
import { formatFileSize } from '@/experiences/sales-portal/blocs/utils/files';
import { toSentenceCase } from '@/experiences/sales-portal/blocs/utils/text';
import { UploadPhotoOrderGuideProps } from '@/experiences/sales-portal/components/create-order-guide/upload-photo-order-guide.types';

const BODY_MIN_HEIGHT = 280;
const BODY_MAX_HEIGHT = 480;
const ICON_CONTAINER_SIZE = 40;
const DROPZONE_ICON_SIZE = 22;
const THUMBNAIL_SIZE = 40;
const ACCEPTED_FILE_TYPES =
  'image/jpeg,image/png,image/heic,image/heif,application/pdf';

const PROCESSING_MESSAGE_KEY: Partial<
  Record<CreateOrderGuideFromImageStep, string>
> = {
  [CreateOrderGuideFromImageStep.Generating]:
    'sales_portal.create_order_guide.upload_photo.generating',
  [CreateOrderGuideFromImageStep.Scanning]:
    'sales_portal.create_order_guide.upload_photo.scanning',
  [CreateOrderGuideFromImageStep.Uploading]:
    'sales_portal.create_order_guide.upload_photo.uploading',
};

export const UploadPhotoOrderGuide: React.FC<UploadPhotoOrderGuideProps> = ({
  isOpen,
  items,
  onAddImages,
  onBack,
  onClose,
  onGenerate,
  onRemoveImage,
  onScan,
  restaurantName,
  selectedImages,
  step,
}) => {
  const theme = useTheme();
  const [styles] = useStyles();
  const [previews, setPreviews] = useState<(string | null)[]>([]);
  const previewsRef = useRef<(string | null)[]>([]);
  const [rejectedFileNames, setRejectedFileNames] = useState<string[]>([]);

  // Revoke URLs that outlive an explicit close (e.g. navigate-away on generate).
  useEffect(
    () => () =>
      previewsRef.current.forEach((url) => url && URL.revokeObjectURL(url)),
    [],
  );

  const setPreviewUrls = (urls: (string | null)[]) => {
    previewsRef.current = urls;
    setPreviews(urls);
  };

  const handleAddImages = (images: File[]) => {
    if (images.length === 0) {
      return;
    }
    const existingKeys = new Set(selectedImages.map(fileKey));
    const added = images
      .filter((image) => {
        const key = fileKey(image);
        if (existingKeys.has(key)) {
          return false;
        }
        existingKeys.add(key);
        return true;
      })
      .map((image) =>
        image.type.startsWith('image/') ? URL.createObjectURL(image) : null,
      );
    onAddImages(images);
    setPreviewUrls([...previews, ...added]);
    setRejectedFileNames([]);
  };

  const handleRejectedFiles = (files: File[]) => {
    setRejectedFileNames(files.map((file) => file.name));
  };

  const handleRemoveImage = (index: number) => {
    const url = previews[index];
    if (url) {
      URL.revokeObjectURL(url);
    }
    onRemoveImage(index);
    setPreviewUrls(
      previews.filter((_, previewIndex) => previewIndex !== index),
    );
  };

  const handleClose = () => {
    previews.forEach((url) => url && URL.revokeObjectURL(url));
    setPreviewUrls([]);
    setRejectedFileNames([]);
    onClose();
  };

  const isProcessing =
    step === CreateOrderGuideFromImageStep.Uploading ||
    step === CreateOrderGuideFromImageStep.Scanning ||
    step === CreateOrderGuideFromImageStep.Generating;

  // `Dialog.Header`'s `headerAction` defaults to a close (X) button.
  // `undefined` keeps that default, `null` hides it while processing.
  const closeButton = isProcessing ? null : undefined;

  const generateLabel = i18n.t(
    items.length === 1
      ? 'sales_portal.create_order_guide.upload_photo.generate'
      : 'sales_portal.create_order_guide.upload_photo.generate_plural',
    { count: items.length },
  );

  const showBanner = rejectedFileNames.length > 0;
  const unsupportedReason = i18n.t(
    'sales_portal.create_order_guide.upload_photo.unsupported_file_reason',
  );
  const rejectedBanner = showBanner ? (
    <XStack
      alignItems="flex-start"
      backgroundColor={theme.sk.color.bg.error}
      borderRadius={theme.sk.radii.radiusMedium}
      gap={theme.sk.spacing.spaceSmall}
      padding={theme.sk.spacing.spaceSMedium}
      role="alert"
    >
      <Icons.AlertCircle
        color={theme.sk.color.icon.negative.primary}
        size="$18"
      />
      <YStack flex={1} gap={2}>
        <Typography
          color={theme.sk.color.text.error}
          fontWeight="$medium"
          size="$sm"
        >
          {i18n.t(
            rejectedFileNames.length === 1
              ? 'sales_portal.create_order_guide.upload_photo.unsupported_files_title'
              : 'sales_portal.create_order_guide.upload_photo.unsupported_files_title_plural',
            { count: rejectedFileNames.length },
          )}
        </Typography>
        {rejectedFileNames.map((name, index) => (
          <Typography
            color={theme.sk.color.text.error}
            key={`${name}-${index}`}
            size="$xs"
          >
            {name} — {unsupportedReason}
          </Typography>
        ))}
      </YStack>
      <IconButton
        aria-label={i18n.t('dismiss')}
        icon={<Icons.X />}
        onPress={() => setRejectedFileNames([])}
        shape="rounded"
        size="$sm"
        variant="tertiary"
      />
    </XStack>
  ) : null;

  const dropzone = (
    <FileDropzone
      accept={ACCEPTED_FILE_TYPES}
      inputLabel={i18n.t(
        'sales_portal.create_order_guide.upload_photo.file_input_label',
      )}
      multiple
      onFiles={handleAddImages}
      onRejectedFiles={handleRejectedFiles}
    >
      <YStack
        alignItems="center"
        height={ICON_CONTAINER_SIZE}
        justifyContent="center"
        width={ICON_CONTAINER_SIZE}
      >
        <Icons.Camera
          color={theme.sk.color.icon.primary}
          size={DROPZONE_ICON_SIZE}
        />
      </YStack>
      <Typography
        color={theme.sk.color.text.primary}
        fontWeight="$medium"
        size="$sm"
        textDecorationLine="underline"
      >
        {i18n.t('sales_portal.create_order_guide.upload_photo.dropzone_prompt')}
      </Typography>
      <Typography color={theme.sk.color.text.tertiary} size="$xs">
        {i18n.t('sales_portal.create_order_guide.upload_photo.dropzone_hint')}
      </Typography>
    </FileDropzone>
  );

  const selectedImagesList = (
    <YStack gap={theme.sk.spacing.spaceSmall}>
      {selectedImages.map((image, index) => (
        <XStack
          alignItems="center"
          backgroundColor={theme.sk.color.bg.primary.default}
          borderColor={theme.sk.color.border.primary}
          borderRadius={theme.sk.radii.radiusMedium}
          borderWidth={1}
          gap={theme.sk.spacing.spaceSMedium}
          key={`${image.name}-${index}`}
          paddingLeft={theme.sk.spacing.spaceSMedium}
          paddingRight={theme.sk.spacing.spaceSmall}
          paddingVertical={theme.sk.spacing.spaceSmall}
        >
          {previews[index] ? (
            <Image
              accessibilityLabel=""
              resizeMode="cover"
              source={{ uri: previews[index] as string }}
              style={styles.thumbnail}
            />
          ) : (
            <Icons.Check
              color={theme.sk.color.icon.positive.primary}
              size="$18"
            />
          )}
          <YStack flex={1}>
            <Typography
              color={theme.sk.color.text.primary}
              fontWeight="$medium"
              numberOfLines={1}
              size="$sm"
            >
              {image.name}
            </Typography>
            <Typography color={theme.sk.color.text.tertiary} size="$xs">
              {formatFileSize(image.size)}
            </Typography>
          </YStack>
          <View
            accessibilityLabel={i18n.t(
              'sales_portal.create_order_guide.upload_photo.remove_file',
              { name: image.name },
            )}
            accessibilityRole="button"
            cursor="pointer"
            onPress={() => handleRemoveImage(index)}
          >
            <Icons.X color={theme.sk.color.icon.secondary} size="$16" />
          </View>
        </XStack>
      ))}
    </YStack>
  );

  const cancelButton = (
    <Button onPress={handleClose} size="$md" variant="tertiary">
      {i18n.t('cancel')}
    </Button>
  );

  const renderStep = (): {
    body: JSX.Element;
    bodyOwnsScroll?: boolean;
    footer: JSX.Element | null;
    footerFade: boolean;
  } => {
    if (isProcessing) {
      return {
        body: (
          <YStack
            accessibilityRole="progressbar"
            alignItems="center"
            justifyContent="center"
            minHeight={BODY_MIN_HEIGHT}
          >
            <ShimmerText size="$md">
              {i18n.t(PROCESSING_MESSAGE_KEY[step] ?? '')}
            </ShimmerText>
          </YStack>
        ),
        footer: null,
        footerFade: false,
      };
    }

    if (step === CreateOrderGuideFromImageStep.GenerationFailed) {
      return {
        body: (
          <YStack
            alignItems="center"
            justifyContent="center"
            minHeight={BODY_MIN_HEIGHT}
          >
            <ErrorState
              description={i18n.t(
                'sales_portal.create_order_guide.generation_failed_description',
              )}
              icon={
                <Icons.CircleDashed
                  color={theme.sk.color.icon.primary}
                  size="$48"
                  strokeWidth={1}
                />
              }
              onPress={onGenerate}
              title={i18n.t(
                'sales_portal.create_order_guide.generation_failed_title',
              )}
            />
          </YStack>
        ),
        footer: null,
        footerFade: false,
      };
    }

    if (step === CreateOrderGuideFromImageStep.Review) {
      if (items.length === 0) {
        return {
          body: (
            <YStack
              alignItems="center"
              justifyContent="center"
              minHeight={BODY_MIN_HEIGHT}
            >
              <EmptyState
                description={i18n.t(
                  'sales_portal.create_order_guide.upload_photo.no_items_message',
                )}
                iconElement={
                  <Icons.CircleDashed
                    color={theme.sk.color.icon.primary}
                    size="$48"
                    strokeWidth={1}
                  />
                }
                title={i18n.t(
                  'sales_portal.create_order_guide.upload_photo.no_items_title',
                )}
              />
            </YStack>
          ),
          footer: (
            <>
              {cancelButton}
              <Button onPress={onBack} size="$md" variant="primary">
                {i18n.t('back')}
              </Button>
            </>
          ),
          footerFade: false,
        };
      }
      return {
        body: (
          <List>
            <FlatList
              data={items}
              keyExtractor={(item, index) => `${item.name}-${index}`}
              renderItem={({ item }) => (
                <List.Item>
                  <List.Item.Content>
                    <Typography
                      color={theme.sk.color.text.primary}
                      fontWeight="$medium"
                      size="$sm"
                    >
                      {toSentenceCase(item.name)}
                    </Typography>
                    <Typography
                      color={theme.sk.color.text.secondary}
                      size="$xs"
                    >
                      {item.ingredients.map(toSentenceCase).join(', ')}
                    </Typography>
                  </List.Item.Content>
                </List.Item>
              )}
              style={{ maxHeight: BODY_MAX_HEIGHT }}
            />
          </List>
        ),
        bodyOwnsScroll: true,
        footer: (
          <>
            {cancelButton}
            <Button onPress={onGenerate} size="$md" variant="primary">
              {generateLabel}
            </Button>
          </>
        ),
        footerFade: true,
      };
    }

    return {
      body: (
        <YStack gap={theme.sk.spacing.spaceMedium}>
          <Typography color={theme.sk.color.text.secondary} size="$sm">
            {i18n.t('sales_portal.create_order_guide.upload_photo.description')}
          </Typography>
          {rejectedBanner}
          {dropzone}
          {selectedImages.length > 0 ? selectedImagesList : null}
        </YStack>
      ),
      footer: (
        <>
          {cancelButton}
          <Button
            disabled={selectedImages.length === 0}
            onPress={onScan}
            size="$md"
            variant="primary"
          >
            {i18n.t('sales_portal.create_order_guide.upload_photo.continue')}
          </Button>
        </>
      ),
      footerFade: false,
    };
  };

  const { body, footer, footerFade, bodyOwnsScroll } = renderStep();

  return (
    <Dialog
      onOpenChange={(open) => {
        if (!open && !isProcessing) {
          handleClose();
        }
      }}
      open={isOpen}
    >
      <Dialog.Content>
        <Dialog.Header headerAction={closeButton}>
          <YStack gap={2}>
            <Dialog.Title>
              {i18n.t('sales_portal.create_order_guide.upload_photo_or_file')}
            </Dialog.Title>
            {restaurantName ? (
              <Typography color={theme.sk.color.text.secondary} size="$sm">
                {restaurantName}
              </Typography>
            ) : null}
          </YStack>
        </Dialog.Header>
        {bodyOwnsScroll ? body : <Dialog.Body>{body}</Dialog.Body>}
        {footer ? (
          <Dialog.Footer fade={footerFade}>{footer}</Dialog.Footer>
        ) : null}
      </Dialog.Content>
    </Dialog>
  );
};

const useStyles = createStyles((theme) => ({
  thumbnail: {
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
    height: THUMBNAIL_SIZE,
    width: THUMBNAIL_SIZE,
  },
}));
